import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, EffectFade } from "swiper/modules";
import { fetchTourBySlug, fetchDestinations } from '../../services/api';

function DestinationDetailsMain() {
    const [activeTab, setActiveTab] = useState("day-tab1");
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const { slug } = useParams();
    const [destination, setDestination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [relatedTours, setRelatedTours] = useState([]);

    useEffect(() => {
        if (slug) {
            loadDestination(slug);
        }
        loadCountries();
    }, [slug]);

    // Load related tours when destination country is available
    useEffect(() => {
        if (destination?.country?.name) {
            loadRelatedTours(destination.country.name);
        }
    }, [destination]);

    const loadRelatedTours = async (countryName) => {
        try {
            const response = await fetchDestinations(1, '', countryName);
            // Filter out the current destination and limit to 3 tours
            const tours = response.destinations
                .filter(tour => tour.slug !== slug)
                .slice(0, 3);
            setRelatedTours(tours);
        } catch (err) {
            console.error('Error loading related tours:', err);
        }
    };

    const loadCountries = async () => {
        try {
            // Fetch first page to get total pages
            const firstResponse = await fetchDestinations(1, '', '');
            const totalPages = firstResponse.pagination.meta.last_page || 1;
            let allDestinations = [...firstResponse.destinations];

            // Fetch remaining pages if there are more
            if (totalPages > 1) {
                const remainingPromises = [];
                for (let page = 2; page <= totalPages; page++) {
                    remainingPromises.push(fetchDestinations(page, '', ''));
                }

                const remainingResponses = await Promise.all(remainingPromises);
                remainingResponses.forEach(response => {
                    allDestinations = [...allDestinations, ...response.destinations];
                });
            }

            // Extract unique countries with count
            const countryMap = new Map();

            allDestinations.forEach(dest => {
                if (dest.country && dest.country.name) {
                    const countryName = dest.country.name;
                    if (!countryMap.has(countryName)) {
                        countryMap.set(countryName, {
                            name: countryName,
                            count: 1
                        });
                    } else {
                        const existing = countryMap.get(countryName);
                        existing.count += 1;
                    }
                }
            });

            // Convert to array and sort by name
            const countriesArray = Array.from(countryMap.values())
                .sort((a, b) => a.name.localeCompare(b.name));

            setCountries(countriesArray);
        } catch (err) {
            console.error('Error loading countries:', err);
        }
    };

    const loadDestination = async (slug) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchTourBySlug(slug);

            setDestination(response.tour);
        } catch (err) {
            setError(err.message || 'Failed to load destination. Please try again later.');
            console.error('Error loading destination:', err);
        } finally {
            setLoading(false);
        }
    };

    // Convert rating from "3_star" format to number
    const getRatingNumber = (rating) => {
        if (!rating) return 4.8;
        const match = rating.match(/(\d+)_star/);
        return match ? parseFloat(match[1]) : 4.8;
    };
    // Generate days array from itinerary
    const getDaysFromItinerary = () => {
        if (!destination || !destination.itinerary || destination.itinerary.length === 0) {
            return [{ id: "day-tab1", label: "Day 01" }];
        }
        return destination.itinerary.map((item, index) => ({
            id: `day-tab${index + 1}`,
            label: `Day ${String(index + 1).padStart(2, '0')}`
        }));
    };

    // Get slider images from itinerary or use placeholder
    const getSliderImages = () => {
        if (!destination) return [];
        const images = [];
        if (destination.banner_image) images.push(destination.banner_image);
        if (destination.thumbnail_image && destination.thumbnail_image !== destination.banner_image) {
            images.push(destination.thumbnail_image);
        }
        // Add itinerary images if available
        if (destination.itinerary && destination.itinerary.length > 0) {
            destination.itinerary.forEach(item => {
                if (item.image && !images.includes(item.image)) {
                    images.push(item.image);
                }
            });
        }
        // If no images, use placeholder
        if (images.length === 0) {
            images.push("/assets/img/gallery/gallery_6_1.jpg");
        }
        return images;
    };

    if (loading) {
        return (
            <section className="space">
                <div className="container">
                    <div className="text-center py-5">
                        <p>Loading destination details...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !destination) {
        return (
            <section className="space">
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        <strong>Error:</strong> {error || 'Destination not found!'}
                    </div>
                </div>
            </section>
        );
    }

    const sliderImages = getSliderImages();
    const days = getDaysFromItinerary();
    const rating = getRatingNumber(destination.rating);
    return (
        <section className="space">
            <div className="container">
                <div className="row">
                    <div className="col-xxl-8 col-lg-7">
                        <div className="tour-page-single">
                            <div className="slider-area tour-slider1">
                                {sliderImages.length > 0 && (
                                    <>
                                        <Swiper
                                            modules={[Navigation, Thumbs, EffectFade]}
                                            effect="fade"
                                            loop={sliderImages.length > 1}
                                            spaceBetween={10}
                                            navigation={{
                                                prevEl: ".slider-prev",
                                                nextEl: ".slider-next",
                                            }}
                                            thumbs={{ swiper: thumbsSwiper }}
                                            className="swiper th-slider mb-4"
                                            id="destinationSlider"
                                        >
                                            {sliderImages.map((img, index) => (
                                                <SwiperSlide key={index}>
                                                    <div className="tour-slider-img">
                                                        <img src={img} alt={`Slide ${index + 1}`} />
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        {sliderImages.length > 1 && (
                                            <Swiper
                                                modules={[Navigation, Thumbs]}
                                                loop={true}
                                                spaceBetween={25}
                                                slidesPerView={Math.min(3, sliderImages.length)}
                                                watchSlidesProgress
                                                onSwiper={setThumbsSwiper}
                                                className="swiper th-slider tour-thumb-slider"
                                            >
                                                {sliderImages.map((img, index) => (
                                                    <SwiperSlide key={index}>
                                                        <div className="tour-slider-img">
                                                            <img src={img} alt={`Thumbnail ${index + 1}`} />
                                                        </div>
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        )}
                                        <button
                                            data-slider-prev="#destinationSlider"
                                            className="slider-arrow style3 slider-prev"
                                        >
                                            <img src="/assets/img/icon/hero-arrow-left.svg" alt="" />
                                        </button>
                                        <button
                                            data-slider-next="#destinationSlider"
                                            className="slider-arrow style3 slider-next"
                                        >
                                            <img src="/assets/img/icon/hero-arrow-right.svg" alt="" />
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="page-content d-block">
                                <div className="page-meta mt-50 mb-45">
                                    {destination.is_featured === 1 && (
                                        <Link className="page-tag mr-5" to="/destinations">
                                            Featured
                                        </Link>
                                    )}
                                    <span className="ratting">
                                        <i className="fa-sharp fa-solid fa-star" />
                                        <span>{rating}</span>
                                    </span>
                                </div>
                                {destination.sub_title && (
                                    <p className="destination-subtitle mb-2" style={{ fontSize: '1.1rem', color: '#666' }}>
                                        {destination.sub_title}
                                    </p>
                                )}
                                <h2 className="box-title">
                                    {destination.title}
                                </h2>
                                {destination.description && (
                                    <div
                                        className="blog-text mb-30"
                                        dangerouslySetInnerHTML={{ __html: destination.description }}
                                    />
                                )}
                                <h2 className="box-title">Basic Information</h2>
                                <div className="destination-checklist">
                                    <div className="checklist style2">
                                        <ul>
                                            <li>Destination</li>
                                            <li>Duration</li>
                                            <li>Country</li>
                                            <li>City</li>
                                            {destination.type && <li>Type</li>}
                                            {destination.price && <li>Per Person</li>}
                                        </ul>
                                    </div>
                                    <div className="checklist style2">
                                        <ul>
                                            <li>{destination.country?.name || 'N/A'}</li>
                                            <li>{destination.duration ? `${destination.duration} Days` : 'N/A'}</li>
                                            <li>{destination.country?.name || 'N/A'}</li>
                                            <li>{destination.city || 'N/A'}</li>
                                            {destination.type && <li>{destination.type}</li>}
                                            {destination.price && <li>${destination.price}</li>}
                                        </ul>
                                    </div>
                                </div>
                                {destination.itinerary && destination.itinerary.length > 0 && (
                                    <>
                                        <h2 className="box-title">Highlights</h2>
                                        <div className="checklist">
                                            <ul>
                                                {destination.itinerary.slice(0, 7).map((item, index) => (
                                                    <li key={index}>{item.title}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                            <h2 className="box-title">Included and Excluded</h2>
                            <div className="destination-checklist">
                                {destination.includes && destination.includes.length > 0 && (
                                    <div className="checklist style2 style4">
                                        <ul>
                                            {destination.includes.map((item, index) => (
                                                <li key={index}>{item.title}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {destination.excludes && destination.excludes.length > 0 && (
                                    <div className="checklist style5">
                                        <ul>
                                            {destination.excludes.map((item, index) => (
                                                <li key={index}>{item.title}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            {destination.itinerary && destination.itinerary.length > 0 && (
                                <>
                                    <h3 className="page-title mt-50 mb-0">Tour Plan</h3>
                                    <div>
                                        {/* Tab Navigation */}
                                        <ul className="nav nav-tabs tour-tab mt-10" role="tablist">
                                            {days.map((day) => (
                                                <li className="nav-item" key={day.id} role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTab === day.id ? "active" : ""}`}
                                                        onClick={() => setActiveTab(day.id)}
                                                        type="button"
                                                        role="tab"
                                                        aria-controls={day.id + "-pane"}
                                                        aria-selected={activeTab === day.id}
                                                    >
                                                        {day.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* Tab Content */}
                                        <div className="tab-content">
                                            {days.map((day, dayIndex) => {
                                                const itineraryItem = destination.itinerary[dayIndex];
                                                return (
                                                    <div
                                                        key={day.id}
                                                        className={`tab-pane fade ${activeTab === day.id ? "show active" : ""}`}
                                                        id={day.id + "-pane"}
                                                        role="tabpanel"
                                                        aria-labelledby={day.id}
                                                        tabIndex={0}
                                                    >
                                                        <div className="tour-grid-plan">
                                                            {itineraryItem && (
                                                                <>
                                                                    {itineraryItem.image && (
                                                                        <div className="tour-plan-img mb-20">
                                                                            <img src={itineraryItem.image} alt={itineraryItem.title || 'Tour plan'} />
                                                                        </div>
                                                                    )}
                                                                    <h4 className="tour-plan-title mb-15">{itineraryItem.title}</h4>
                                                                    {itineraryItem.description && (
                                                                        <p className="box-text mb-20">{itineraryItem.description}</p>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                            {/* Comment end */} {/* Comment Form */}
                            <div className="th-comment-form ">
                                <div className="row">
                                    <h3 className="blog-inner-title h4 mb-2">Leave a Reply</h3>
                                    <p className="mb-25">
                                        Your email address will not be published. Required fields are
                                        marked
                                    </p>
                                    <div className="col-md-6 form-group">
                                        <input
                                            type="text"
                                            placeholder="Full Name*"
                                            className="form-control"
                                            required=""
                                        />
                                        <i className="far fa-user" />
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <input
                                            type="text"
                                            placeholder="Your Email*"
                                            className="form-control"
                                            required=""
                                        />
                                        <i className="far fa-envelope" />
                                    </div>
                                    <div className="col-12 form-group">
                                        <input
                                            type="text"
                                            placeholder="Website"
                                            className="form-control"
                                            required=""
                                        />
                                        <i className="far fa-globe" />
                                    </div>
                                    <div className="col-12 form-group">
                                        <textarea
                                            placeholder="Comment*"
                                            className="form-control"
                                            defaultValue={""}
                                        />
                                        <i className="far fa-pencil" />
                                    </div>
                                    <div className="col-12 form-group">
                                        <input type="checkbox" id="html" />
                                        <label htmlFor="html">
                                            Save my name, email, and website in this browser for the next
                                            time I comment.
                                        </label>
                                    </div>
                                    <div className="col-12 form-group mb-0">
                                        <button className="th-btn">
                                            Send Message
                                            <img src="/assets/img/icon/plane2.svg" alt="" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-4 col-lg-5">
                        <aside className="sidebar-area style3">
                            <div className="widget widget_search  ">
                                <form className="search-form">
                                    <input type="text" placeholder="Search" />
                                    <button type="submit">
                                        <i className="far fa-search" />
                                    </button>
                                </form>
                            </div>
                            <div className="widget widget_categories">
                                <h3 className="widget_title">Countries</h3>
                                <ul>
                                    {countries.length === 0 ? (
                                        <li>
                                            <span>Loading countries...</span>
                                        </li>
                                    ) : (
                                        countries.map((country, index) => (
                                            <li key={index}>
                                                <Link to={`/destinations?country=${encodeURIComponent(country.name)}`}>
                                                    <img src="/assets/img/theme-img/map.svg" alt="" />
                                                    {country.name}
                                                </Link>
                                                <span>({country.count})</span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                            <div className="widget">
                                <h3 className="widget_title">
                                    {destination?.country?.name ? `More Tours in ${destination.country.name}` : 'Related Tours'}
                                </h3>
                                <div className="recent-post-wrap">
                                    {relatedTours.length === 0 ? (
                                        <p className="text-muted">No other tours available in this country.</p>
                                    ) : (
                                        relatedTours.map((tour) => (
                                            <div className="recent-post" key={tour.id}>
                                                <div className="media-img">
                                                    <Link to={`/destinations/${tour.slug}`}>
                                                        <img
                                                            src={tour.thumbnail_image || '/assets/img/blog/recent-post-1-1.jpg'}
                                                            alt={tour.title}
                                                        />
                                                    </Link>
                                                </div>
                                                <div className="media-body">
                                                    <h4 className="post-title">
                                                        <Link className="text-inherit" to={`/destinations/${tour.slug}`}>
                                                            {tour.title}
                                                        </Link>
                                                    </h4>
                                                    <div className="recent-post-meta">
                                                        <span>
                                                            <i className="fa-light fa-clock" />
                                                            {tour.duration ? `${tour.duration} Days` : 'N/A'}
                                                        </span>
                                                        {tour.price && (
                                                            <span style={{ marginLeft: '10px' }}>
                                                                <i className="fa-light fa-dollar-sign" />
                                                                {tour.price}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                        </aside>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default DestinationDetailsMain
